// frontend/src/pages/MetaCubeOracleV2Page.jsx
import React, { useState, useRef, useEffect } from "react";
import MetaCubeOracleV2 from "../components/MetaCubeOracleV2";
import "../styles/metacube_oracle_v2.css";

export default function MetaCubeOracleV2Page() {
  // États entropie
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  // États curseurs
  const [cubeSpeedPct, setCubeSpeedPct] = useState(80);
  const [ringSpeedPct, setRingSpeedPct] = useState(50);
  const [colorShiftRatePct, setColorShiftRatePct] = useState(30);
  const [surfaceSpeedPct, setSurfaceSpeedPct] = useState(50); // <-- a) nouvel état

  // Options
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  // Sliders Bloom
  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);

  const containerRef = useRef(null);

  // ⚡ Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  // --- Fetch entropie UI en temps réel ---
  useEffect(() => {
    const fetchEntropyUI = async () => {
      try {
        setLoadingUI(true);
        const res = await fetch("/api/geometry/metacube_oracle_v2/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cube_speed: cubeSpeedPct / 100,
            ring_speed: ringSpeedPct / 100,
            color_shift: colorShiftRatePct / 100,
            bloom_intensity: bloomIntensity,
            bloom_radius: bloomRadius,
            bloom_threshold: bloomThreshold,
            chaos,
            glow,
            running,
          }),
        });
const data = await res.json();
if (typeof data.entropy_ui === "number") {
  setEntropyUI(data.entropy_ui);
}

      } catch (err) {
        console.error("Erreur fetch entropy UI:", err);
        setEntropyUI(null);
      } finally {
        setLoadingUI(false);
      }
    };

    fetchEntropyUI();
  }, [
    cubeSpeedPct,
    ringSpeedPct,
    colorShiftRatePct,
    bloomIntensity,
    bloomRadius,
    bloomThreshold,
    chaos,
    glow,
    running,
  ]);

  return (
    <div className="page-container">
      <h1>MetaCube Oracle V2</h1>

      {/* Jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#bb86fc" }}>Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
      </div>

      {/* Contrôles + Visualizer */}
      <div className="controls-row">
        {/* Contrôles */}
        <div className="controls-panel">
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            className={chaos ? "toggle-button active-chaos" : "toggle-button inactive-chaos"}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            className={glow ? "toggle-button active-glow" : "toggle-button inactive-glow"}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          {/* Sliders */}
          <div className="input-group">
            <label>Vitesse Cube : {cubeSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={cubeSpeedPct}
              onChange={(e) => setCubeSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Vitesse Anneaux : {ringSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={ringSpeedPct}
              onChange={(e) => setRingSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Chromatique : {colorShiftRatePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={colorShiftRatePct}
              onChange={(e) => setColorShiftRatePct(Number(e.target.value))}
            />
          </div>

          {/* b) Ajout du slider surfaces */}
          <div className="input-group">
            <label>Vitesse Surfaces : {surfaceSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={surfaceSpeedPct}
              onChange={(e) => setSurfaceSpeedPct(Number(e.target.value))}
            />
          </div>

          {/* Sliders Bloom */}
          <div className="input-group">
            <label>Halo Intensité : {bloomIntensity.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={3}
              step={0.1}
              value={bloomIntensity}
              onChange={(e) => setBloomIntensity(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Rayon : {bloomRadius.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={bloomRadius}
              onChange={(e) => setBloomRadius(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Seuil Luminosité : {bloomThreshold.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bloomThreshold}
              onChange={(e) => setBloomThreshold(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Container Visualizer */}
        <div ref={containerRef} className="visualizer-container">
          <MetaCubeOracleV2
            containerRef={containerRef}
            running={running}
            chaos={chaos}
            glow={glow}
            cubeSpeedPct={cubeSpeedPct}
            ringSpeedPct={ringSpeedPct}
            colorShiftRatePct={colorShiftRatePct}
            surfaceSpeedPct={surfaceSpeedPct} // <-- c) nouvelle prop
            bloomIntensity={bloomIntensity}
            bloomRadius={bloomRadius}
      bloomThreshold={bloomThreshold}
      onEntropyUpdate={(val) => {
        if (typeof val === "number") {
          setEntropyDyn(val);
        }
      }}
    />
  </div>
</div>
    </div>
  );
}
